import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, mapTo } from 'rxjs/operators';
import { AuthWrapperService } from '../../../auth';
import { LoginLogoutActionTypes } from '../../../auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType } from '../../../global-message';
import { GlobalMessageActions } from '../../../global-message/store/actions';
import { normalizeHttpError, withdrawOn } from '../../../util';
import { UserProfileConnector } from '../../connectors/user-profile.connector';
import { UserActions } from '../actions';

@Injectable()
export class CredentialsEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  updateCredentials$: Observable<UserActions.UpdateCredentialsFail | UserActions.UpdateCredentialsSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.UpdateCredentialsActionTypes.UpdateCredentials),
      map((action: UserActions.UpdateCredentials) => action.payload),
      concatMap((payload) => {
        return this.userProfileConnector.updateCredentials(payload.userId, payload.credentials).pipe(
          map(() => new UserActions.UpdateCredentialsSuccess({ credentials: payload.credentials, userId: payload.userId })),
          catchError((error) => of(new UserActions.UpdateCredentialsFail(normalizeHttpError(error))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  handleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.UpdateCredentialsActionTypes.UpdateCredentialsSuccess),
      map((action: UserActions.UpdateCredentialsSuccess) => action.payload.credentials),
      map((credentials) => {
        if (!!credentials.userName) {
          this.authWrapperService.silentAuthorize(credentials.userName, credentials.newPassword || credentials.oldPassword);
        }
        return new GlobalMessageActions.AddMessage({
          text: { key: 'settings.account.updateSuccess_message' },
          type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
        });
      })
    )
  );

  handleFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.UpdateCredentialsActionTypes.UpdateCredentialsFail),
      mapTo(
        new GlobalMessageActions.AddMessage({
          text: { key: 'settings.account.updateFailure_message' },
          type: GlobalMessageType.MSG_TYPE_ERROR,
        })
      )
    )
  );

  constructor(
    private actions$: Actions,
    private userProfileConnector: UserProfileConnector,
    private authWrapperService: AuthWrapperService
  ) {}
}
